import { HoursType } from '@app/shared/types/enums/hours-type';
import { TimeReport, TimeReportStatus, TimeReportType } from '@app/shared/types/models/reports/time-report';
import {
  TimeReportFilters,
  TimeReportKindFilter,
  TimeReportStatusFilter
} from '@app/shared/types/models/reports/time-report-panel.model';
import { TimeReportRowKind, TimeReportRowVM } from '@app/shared/types/models/reports/time-report-row.model';
import { isWorkTimeReport, WorkTimeReport } from '@app/shared/types/models/reports/work-time-report';
import moment from 'moment';

export function selectWorkTimeReports(reports: TimeReport[]): WorkTimeReport[] {
  return reports.filter(isWorkTimeReport);
}

export function resolveTimeReportDialogType(report: TimeReport): TimeReportType | undefined {
  if (report.reportType === 'WorkTime' || report.reportType === 'Absence') {
    return report.reportType;
  }
  return undefined;
}

export function collectRowDateKeys(rows: TimeReportRowVM[]): string[] {
  const dates = new Set<string>();
  for (const row of rows) {
    if (!row.date) {
      continue;
    }
    dates.add(moment(row.date).format('YYYY-MM-DD'));
  }
  return [...dates];
}

export function timeReportRowTypeKey(kind: TimeReportRowKind, typeValue: string | undefined): string | undefined {
  return typeValue ? `${kind}:${typeValue}` : undefined;
}

export function workTimeTypeKey(hoursType: HoursType | undefined): string | undefined {
  return timeReportRowTypeKey('WorkTime', hoursType);
}

export function toTypeFilterOption(row: TimeReportRowVM): { key: string; labelKey: string } | undefined {
  const typeKey = row.typeKey;
  const labelKey = row.typeTranslationKey;
  if (!typeKey || !typeKey.includes(':') || !labelKey) {
    return undefined;
  }
  return { key: typeKey, labelKey };
}

export function isTimeReportRowSelectable(row: TimeReportRowVM): row is TimeReportRowVM & { id: string } {
  return typeof row.id === 'string' && row.id.length > 0 && row.status !== TimeReportStatus.APPROVED;
}

export function collectSelectableRowIds(rows: TimeReportRowVM[]): string[] {
  return rows.filter(isTimeReportRowSelectable).map(row => row.id);
}

/** Explicit approval selection; other report selection flows retain their own eligibility. */
export function collectPendingRowIds(rows: TimeReportRowVM[]): string[] {
  return [
    ...new Set(
      rows
        .filter(isTimeReportRowSelectable)
        .filter(row => row.status === TimeReportStatus.PENDING)
        .map(row => row.id)
    )
  ];
}

export function compareOptionalDatesDesc(left: Date | undefined, right: Date | undefined): number {
  const leftTime = left ? moment(left).valueOf() : undefined;
  const rightTime = right ? moment(right).valueOf() : undefined;
  if (leftTime === undefined && rightTime === undefined) {
    return 0;
  }
  if (leftTime === undefined) {
    return 1;
  }
  if (rightTime === undefined) {
    return -1;
  }
  return rightTime - leftTime;
}

export function compareTimeReportRowsByDateDesc(left: TimeReportRowVM, right: TimeReportRowVM): number {
  return compareOptionalDatesDesc(left.date, right.date);
}

export function matchesWorkTimeChartFilters(report: WorkTimeReport, filters: TimeReportFilters): boolean {
  if (filters.users?.length) {
    const userId = report.user?.()?.id;
    if (!userId || !filters.users.includes(userId)) {
      return false;
    }
  }

  if (filters.dates?.length) {
    const dateKey = report.date ? moment(report.date).format('YYYY-MM-DD') : '';
    if (!filters.dates.includes(dateKey)) {
      return false;
    }
  }

  if (filters.tasks?.length && !filters.tasks.includes(report.taskId)) {
    return false;
  }
  if (filters.projects?.length && !filters.projects.includes(report.projectId)) {
    return false;
  }
  if (filters.locations?.length && !filters.locations.includes(report.locationId)) {
    return false;
  }

  if (filters.types?.length) {
    const typeKey = workTimeTypeKey(report.hoursType);
    if (!typeKey || !filters.types.includes(typeKey)) {
      return false;
    }
  }

  return true;
}

export function matchesTimeReportRowFilters(row: TimeReportRowVM, filters: TimeReportFilters): boolean {
  const kind: TimeReportKindFilter = filters.kind ?? 'All';
  if (kind !== 'All' && row.kind !== kind) {
    return false;
  }

  const status: TimeReportStatusFilter = filters.status ?? 'All';
  if (status !== 'All' && row.status !== status) {
    return false;
  }

  if (filters.users?.length) {
    if (!row.userId || !filters.users.includes(row.userId)) {
      return false;
    }
  }

  if (filters.dates?.length) {
    const dateKey = row.date ? moment(row.date).format('YYYY-MM-DD') : '';
    if (!filters.dates.includes(dateKey)) {
      return false;
    }
  }

  if (filters.companies?.length) {
    if (!row.companyId || !filters.companies.includes(row.companyId)) {
      return false;
    }
  }

  if (filters.types?.length) {
    if (!row.typeKey || !filters.types.includes(row.typeKey)) {
      return false;
    }
  }

  if (filters.projects?.length) {
    if (row.kind !== 'WorkTime' || !row.projectId || !filters.projects.includes(row.projectId)) {
      return false;
    }
  }

  if (filters.tasks?.length) {
    if (row.kind !== 'WorkTime' || !row.task?.id || !filters.tasks.includes(row.task.id)) {
      return false;
    }
  }

  if (filters.locations?.length) {
    if (row.kind !== 'WorkTime' || !row.locationId || !filters.locations.includes(row.locationId)) {
      return false;
    }
  }

  return true;
}
